import React, { useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from 'react-native';

import { ContactType } from '../../api/models/TranscriptRequest';
import { colors, fonts, scale } from '../../Styles';
import { pickerStates } from '../../util/PickerStates';
import { regexValidators } from '../../util/RegexValidators';
import { TranscriptRequestScreenProps } from '../containers/RequestTranscriptContainer';
import { MNOHSButton } from '../MNOHSButton';
import { MNOHSDashboardWrapper } from '../MNOHSDashboardWrapper';
import { MNOHSDateStringInput } from '../MNOHSDataStringInput.web';
import { MNOHSPicker } from '../MNOHSPicker.web';
import { MNOHSTextInput } from '../MNOHSTextInput';
import { TranscriptRequestSuccessScreen } from './RequestTranscriptSuccessScreen';

export const TranscriptRequestScreen: React.FC<TranscriptRequestScreenProps> = ({
  showSuccessScreen,
  isGettingProfile,
  buttonState,
  formState,
  error,
  userPressedSubmit,
  userUpdatedTextInput,
  userPresedRadioButton,
}) => {
  const [formOfContact, setFormOfContact] = useState<ContactType>(formState.contactType);

  const updateRadioButtonSelection = (contactType: ContactType) => {
    setFormOfContact(contactType);
    userPresedRadioButton(contactType);
  };

  /** Sets the background color of the "radio buttons" to help readability of the component. */
  const setButtonColor = (contactType: ContactType) => (formOfContact === contactType ? colors.black : colors.white);

  /** Sets the background color of the "radio buttons" to help readability of the component. */
  const setTextColor = (contactType: ContactType) => (formOfContact === contactType ? colors.white : colors.black);

  return (
    <MNOHSDashboardWrapper headerTitle={'Request Transcript'} padded={true}>
      {showSuccessScreen ? (
        <TranscriptRequestSuccessScreen />
      ) : (
        <>
          <Text style={styles.paragraphText}>To request an official transcript, complete all fields below. </Text>
          <Text style={styles.headerText}>
            The request must be made at least 5 business days in advance of any application deadlines.
          </Text>

          {isGettingProfile && (
            <View style={styles.indicatorContainer}>
              <ActivityIndicator size={scale(20)} style={styles.indicator} />
              <Text style={fonts.descriptionText}>
                {formState.firstName ? 'Updating your prefilled data.' : 'Pre-filling your data.'} Please wait...
              </Text>
            </View>
          )}

          <View style={styles.textInputContainer}>
            <MNOHSTextInput
              title={'First Name'}
              value={formState.firstName}
              titleStyle={_styles.title}
              containerStyle={_styles.inputContainer}
              style={_styles.textInput}
              accessibilityLabel={'First name text input'}
              editable={false}
              floating={true}
              disabled={true}
            />
            <MNOHSTextInput
              title={'Last Name'}
              value={formState.lastName}
              titleStyle={_styles.title}
              containerStyle={_styles.inputContainer}
              style={_styles.textInput}
              accessibilityLabel={'Last name text input'}
              editable={false}
              floating={true}
              disabled={true}
            />
            <MNOHSTextInput
              title={'Email'}
              keyboardType={'email-address'}
              value={formState.email}
              titleStyle={_styles.title}
              containerStyle={_styles.inputContainer}
              style={_styles.textInput}
              accessibilityLabel={'Email text input'}
              editable={false}
              floating={true}
              disabled={true}
            />
            <MNOHSTextInput
              title={'Phone Number'}
              keyboardType={'number-pad'}
              value={formState.phoneNumber}
              onChangeText={value => userUpdatedTextInput('phoneNumber', value)}
              titleStyle={_styles.title}
              containerStyle={_styles.inputContainer}
              style={_styles.textInput}
              accessibilityLabel={'Phone number text input'}
              floating={true}
              required={!Boolean(formState.phoneNumber)}
            />
            {Boolean(formState.phoneNumber) && !formState.phoneNumber.match(regexValidators.phoneNumber) && (
              <Text style={styles.validationError}>&#42; Please enter a valid US phone number and try again.</Text>
            )}

            <MNOHSTextInput
              title={'Name of college/school you want transcripts sent to'}
              value={formState.recipientSchoolName}
              onChangeText={value => userUpdatedTextInput('recipientSchoolName', value)}
              titleStyle={_styles.title}
              containerStyle={_styles.inputContainer}
              style={_styles.textInput}
              accessibilityLabel={'Name of school to send transcript to text input'}
              floating={true}
              required={!Boolean(formState.recipientSchoolName)}
            />

            <View style={_styles.inputContainer}>
              <Text style={styles.addressText}>Address/Fax/Email of college/school you want transcripts sent to</Text>
              <View style={styles.radioContainer}>
                <TouchableOpacity
                  onPress={() => updateRadioButtonSelection(ContactType.MAIL)}
                  style={[styles.radioButton, { backgroundColor: setButtonColor(ContactType.MAIL) }]}>
                  <Text style={{ ...fonts.linkedText, color: setTextColor(ContactType.MAIL) }}>Address</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => updateRadioButtonSelection(ContactType.FAX)}
                  style={[styles.radioButton, { backgroundColor: setButtonColor(ContactType.FAX) }]}>
                  <Text style={{ ...fonts.linkedText, color: setTextColor(ContactType.FAX) }}>Fax</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => updateRadioButtonSelection(ContactType.EMAIL)}
                  style={[styles.radioButton, { backgroundColor: setButtonColor(ContactType.EMAIL) }]}>
                  <Text style={{ ...fonts.linkedText, color: setTextColor(ContactType.EMAIL) }}>Email</Text>
                </TouchableOpacity>
              </View>
            </View>

            {formOfContact === ContactType.MAIL && (
              <>
                <MNOHSTextInput
                  title={'Street Address'}
                  value={formState.collegeAddress.street}
                  onChangeText={value =>
                    userUpdatedTextInput('collegeAddress', { ...formState.collegeAddress, street: value })
                  }
                  titleStyle={_styles.title}
                  containerStyle={_styles.inputContainer}
                  style={_styles.textInput}
                  accessibilityLabel={'Street address of school to send transcript to text input'}
                  floating={true}
                  required={!Boolean(formState.collegeAddress.street)}
                />
                <MNOHSTextInput
                  title={'City'}
                  value={formState.collegeAddress.city}
                  onChangeText={value =>
                    userUpdatedTextInput('collegeAddress', { ...formState.collegeAddress, city: value })
                  }
                  titleStyle={_styles.title}
                  containerStyle={_styles.inputContainer}
                  style={_styles.textInput}
                  accessibilityLabel={'City of school to send transcript to text input'}
                  floating={true}
                  required={!Boolean(formState.collegeAddress.city)}
                />

                <View style={styles.statesPicker}>
                  <MNOHSPicker
                    options={pickerStates}
                    title={'State'}
                    selectedValue={formState.collegeAddress.state || '--'}
                    onValueChange={value => {
                      if (value !== '--') {
                        userUpdatedTextInput('collegeAddress', { ...formState.collegeAddress, state: value });
                      }
                    }}
                    style={{ flex: 1, marginRight: 8 }}
                    accessibilityLabel={'State of school to send transcript to text input'}
                    required={!Boolean(formState.collegeAddress.state)}
                  />
                  <MNOHSTextInput
                    title={'Zip'}
                    value={formState.collegeAddress.zip}
                    onChangeText={value =>
                      userUpdatedTextInput('collegeAddress', { ...formState.collegeAddress, zip: value })
                    }
                    titleStyle={_styles.title}
                    containerStyle={{ flex: 1 }}
                    style={{ minWidth: 0 }}
                    accessibilityLabel={'Zipcode of school to send transcript to text input'}
                    floating={true}
                    required={!Boolean(formState.collegeAddress.zip)}
                  />
                </View>
                {Boolean(formState.collegeAddress.zip) &&
                  !formState.collegeAddress.zip.match(regexValidators.zipCode) && (
                    <Text style={styles.validationError}>&#42; Please enter a valid zipcode and try again.</Text>
                  )}
              </>
            )}
            {formOfContact === ContactType.FAX && (
              <>
                <MNOHSTextInput
                  keyboardType={'number-pad'}
                  title={'College/School Fax Number'}
                  value={formState.collegeFax}
                  onChangeText={value => userUpdatedTextInput('collegeFax', value)}
                  titleStyle={_styles.title}
                  containerStyle={_styles.inputContainer}
                  style={_styles.textInput}
                  accessibilityLabel={'Address of school to send transcript to text input'}
                  floating={true}
                  required={!Boolean(formState.collegeFax)}
                />
                {Boolean(formState.collegeFax) && !formState.collegeFax.match(regexValidators.fax) && (
                  <Text style={styles.validationError}>&#42; Please enter a valid fax number and try again.</Text>
                )}
              </>
            )}
            {formOfContact === ContactType.EMAIL && (
              <>
                <MNOHSTextInput
                  keyboardType={'email-address'}
                  title={'College/School Email Address'}
                  value={formState.collegeEmail}
                  onChangeText={value => userUpdatedTextInput('collegeEmail', value)}
                  titleStyle={_styles.title}
                  containerStyle={_styles.inputContainer}
                  style={_styles.textInput}
                  accessibilityLabel={'Address of school to send transcript to text input'}
                  floating={true}
                  required={!Boolean(formState.collegeEmail)}
                />
                {Boolean(formState.collegeEmail) && !formState.collegeEmail.match(regexValidators.email) && (
                  <Text style={styles.validationError}>&#42; Please enter a valid email address and try again.</Text>
                )}
              </>
            )}
            <Text style={styles.addressText}>Application Deadline</Text>
            <MNOHSDateStringInput
              format={'YYYY-MM-DD'}
              onValueChange={value => userUpdatedTextInput('applicationDeadline', value)}
              value={formState.applicationDeadline}
              minimumDate={new Date()}
            />
          </View>

          {Boolean(error) && <Text style={styles.errorText}>{error}</Text>}
          <MNOHSButton
            title="SUBMIT"
            state={buttonState}
            onPress={userPressedSubmit}
            style={_styles.button}
            accessibilityLabel="Submit"
          />
        </>
      )}
    </MNOHSDashboardWrapper>
  );
};

// styles for the web
// if you wrap styling for MNOHSTextInput inside StyleSheet.create
// it doesn't work for some reason
const _styles = {
  textInput: {
    flex: 1,
    paddingLeft: scale(8),
  },
  inputContainer: {
    flex: 1,
    marginBottom: scale(24),
  },
  title: {},
  button: {
    marginTop: scale(24),
    marginBottom: scale(42),
  },
};

const styles = StyleSheet.create({
  addressText: {
    ...fonts.linkedText,
    marginBottom: '4%',
  },

  container: {
    height: '100vh',
  },
  errorText: {
    ...fonts.linkedText,
    color: colors.science,
    alignSelf: 'center',
    maxWidth: '90%',
    marginVertical: '5%',
  },
  headerText: {
    ...fonts.linkedText,
    textAlign: 'center',
    alignSelf: 'center',
    maxWidth: '90%',
  },
  paragraphText: {
    ...fonts.paragraph,
    marginBottom: '5%',
    textAlign: 'center',
  },
  radioButton: {
    padding: scale(5),
    borderRadius: scale(8),
    borderColor: colors.black,
    borderWidth: 1,
    width: '31%', // 3 items => 100 / 3 but smaller to have gaps in between
    alignItems: 'center',
  },
  radioContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
    flexDirection: 'row',
  },
  statesPicker: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: scale(24),
  },
  textInputContainer: {
    marginTop: '10%',
    marginBottom: '5%',
    width: '100%',
  },
  validationError: {
    ...fonts.descriptionText,
    color: colors.science,
    marginTop: scale(-16), // marginBottom for textInput set above is 24
    marginBottom: '5%',
  },
  indicatorContainer: {
    marginTop: scale(16),
    marginBottom: scale(-16),
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'center',
  },
  indicator: {
    marginRight: scale(5),
  },
});
